import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/services/i18n';
import { userAPI, templateAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Home, User, Upload, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [uploadingFormat, setUploadingFormat] = useState(false);
  const fileRef = useRef(null);
  const formatRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    subject: '',
    class_name: '',
    mobile_no: '',
    school_name: '',
    profile_picture: '',
    default_template_id: '',
    preferred_language: 'en',
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        subject: user.subject || '',
        class_name: user.class_name || '',
        mobile_no: user.mobile_no || '',
        school_name: user.school_name || '',
        profile_picture: user.profile_picture || '',
        default_template_id: user.default_template_id || '',
        preferred_language: user.preferred_language || 'en',
      });
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await templateAPI.getAll();
        setTemplates(data);
      } catch {
        // ignore
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, default_template_id: form.default_template_id || null };
      const { data } = await userAPI.updateProfile(payload);
      updateUser(data);
      if (data.preferred_language && (data.preferred_language === 'en' || data.preferred_language === 'hi')) setLang(data.preferred_language);
      toast.success('Profile saved!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await userAPI.uploadPicture(file);
      setForm({ ...form, profile_picture: data.profile_picture });
      toast.success('Picture uploaded');
    } catch { toast.error('Upload failed'); }
  };

  const handleFormatUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFormat(true);
    try {
      const { data } = await templateAPI.upload(file);
      const { data: all } = await templateAPI.getAll();
      setTemplates(all);
      setForm({ ...form, default_template_id: data.template_id });
      toast.success('Format uploaded and set as default');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload format');
    } finally {
      setUploadingFormat(false);
      if (formatRef.current) formatRef.current.value = '';
    }
  };

  const picUrl = form.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${form.profile_picture}` : null;

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]">
            <Home className="w-5 h-5 mr-2" /> {t('home')}
          </Button>
          <h1 className="text-xl font-bold text-[#1C1917]">{t('yourProfile')}</h1>
          <Button data-testid="logout-button" variant="ghost" onClick={async () => { await logout(); navigate('/login'); }}>
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <Card className="p-8 border border-stone-200">
          {!user?.profile_completed && (
            <div className="mb-6 p-4 border border-[#C53030] bg-red-50 rounded-md">
              <p className="text-sm font-medium text-[#1C1917]">Welcome! Please complete your profile to continue.</p>
            </div>
          )}
          <div className="flex items-center gap-6 mb-6">
            <div className="relative">
              {picUrl ? (
                <img src={picUrl} alt="avatar" className="w-24 h-24 rounded-full object-cover border-2 border-stone-200" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center border-2 border-stone-200">
                  <User className="w-10 h-10 text-stone-400" />
                </div>
              )}
            </div>
            <div>
              <input ref={fileRef} data-testid="profile-picture-input" type="file" accept="image/*" onChange={handlePicUpload} className="hidden" />
              <Button type="button" data-testid="upload-picture-button" variant="outline" onClick={() => fileRef.current?.click()} className="border border-stone-200">
                <Upload className="w-4 h-4 mr-2" /> Upload Picture
              </Button>
              <p className="text-xs text-stone-500 mt-2">JPG or PNG, up to 5MB</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('teacherName')}</Label>
              <Input id="name" data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="subject">{t('subject')}</Label>
                <Input id="subject" data-testid="profile-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g., Mathematics" required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="class_name">{t('class')}</Label>
                <Input id="class_name" data-testid="profile-class-input" value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} placeholder="e.g., Grade 5" required className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="mobile_no">{t('mobile')}</Label>
              <Input id="mobile_no" data-testid="profile-mobile-input" value={form.mobile_no} onChange={(e) => setForm({ ...form, mobile_no: e.target.value })} placeholder="+91 98765 43210" required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="school_name">{t('schoolName')}</Label>
              <Input id="school_name" data-testid="profile-school-input" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} placeholder="Your school's name" required className="mt-1" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-stone-200">
              <div>
                <Label>{t('languagePreference')}</Label>
                <Select value={form.preferred_language} onValueChange={(v) => { setForm({ ...form, preferred_language: v }); if (v === 'en' || v === 'hi') setLang(v); }}>
                  <SelectTrigger data-testid="profile-language-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">हिन्दी (Hindi)</SelectItem>
                    <SelectItem value="sa">संस्कृतम् (Sanskrit)</SelectItem>
                    <SelectItem value="ta">தமிழ் (Tamil)</SelectItem>
                    <SelectItem value="es">Español (Spanish)</SelectItem>
                    <SelectItem value="fr">Français (French)</SelectItem>
                    <SelectItem value="zh">中文 (Mandarin)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500 mt-1">Interface is available in English and Hindi. AI-generated lesson content will appear in your chosen language.</p>
              </div>
              <div>
                <Label>{t('defaultFormat')}</Label>
                <Select value={form.default_template_id || 'none'} onValueChange={(v) => setForm({ ...form, default_template_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="profile-template-select" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('noFormat')}</SelectItem>
                    {templates.map((tmp) => (
                      <SelectItem key={tmp.template_id} value={tmp.template_id}>{tmp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-stone-500 -mt-2">{t('defaultFormatHint')}</p>
            <input ref={formatRef} data-testid="profile-format-upload-input" type="file" accept=".docx,.pdf" onChange={handleFormatUpload} className="hidden" />
            <Button type="button" data-testid="upload-format-button" variant="outline" onClick={() => formatRef.current?.click()} disabled={uploadingFormat} className="border border-stone-200 w-full">
              <Upload className="w-4 h-4 mr-2" /> {uploadingFormat ? '...' : t('uploadFormat')}
            </Button>

            <Button type="submit" data-testid="save-profile-button" disabled={loading} className="w-full bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md">
              {loading ? t('saving') : t('saveProfile')}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default Profile;
